import React, { useState } from "react";
import FullLogo from "../assets/Full logo.png";
import { useNavigate } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { toast } from "sonner";
import ParseErrors from "../utils/parse-errors";
import axiosInstance from "../utils/axios-instance";

function Login() {
  const [values, setValues] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const validate = () => {
    const newErrors = {};
    if (!values.email) {
      newErrors.email = "Email is required";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(values.email)) {
        newErrors.email = "Enter a valid email address";
      }
    }
    if (!values.password) {
      newErrors.password = "Password is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await axiosInstance.post("/auth/login", values);
      navigate("/feature");
      toast.success(res.data.message);
    } catch (error) {
      toast.error(ParseErrors(error));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setValues({ ...values, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#0f1829] relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full opacity-10 pointer-events-none"
        style={{ background: "radial-gradient(circle, #F05A22 0%, transparent 70%)" }}
      />

      {/* Card */}
      <div className="relative z-10 w-full max-w-[440px] mx-4 bg-[#162035] border border-white/10 rounded-2xl shadow-2xl p-8 sm:p-10">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img
            className="h-10 object-contain cursor-pointer"
            onClick={() => navigate("/")}
            src={FullLogo}
            alt="Martech Mastery"
          />
        </div>

        {/* Heading */}
        <h1 className="text-2xl font-bold text-white text-center mb-1">
          Welcome back
        </h1>
        <p className="text-white/40 text-sm text-center mb-8">
          Sign in to your Martech Mastery account
        </p>

        {/* Form */}
        <div className="flex flex-col gap-4">
          {/* Email */}
          <div>
            <label className="block text-xs font-semibold text-white/50 mb-1.5 tracking-wide uppercase">Email</label>
            <input
              className={`w-full px-4 py-3 rounded-xl border text-white text-sm bg-[#1e2d4a] placeholder-white/20 focus:outline-none focus:ring-2 transition-all ${
                errors.email
                  ? "border-red-500/60 focus:ring-red-500/30"
                  : "border-white/10 focus:ring-[#F05A22]/40 focus:border-[#F05A22]/50"
              }`}
              placeholder="you@company.com"
              name="email"
              value={values.email}
              onChange={handleChange}
            />
            {errors.email && (
              <p className="text-red-400 text-xs mt-1.5 px-1">{errors.email}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-xs font-semibold text-white/50 mb-1.5 tracking-wide uppercase">Password</label>
            <div className="relative">
              <input
                className={`w-full px-4 py-3 pr-11 rounded-xl border text-white text-sm bg-[#1e2d4a] placeholder-white/20 focus:outline-none focus:ring-2 transition-all ${
                  errors.password
                    ? "border-red-500/60 focus:ring-red-500/30"
                    : "border-white/10 focus:ring-[#F05A22]/40 focus:border-[#F05A22]/50"
                }`}
                placeholder="••••••••"
                type={showPassword ? "text" : "password"}
                name="password"
                value={values.password}
                onChange={handleChange}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/70 transition-colors"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>
            {errors.password && (
              <p className="text-red-400 text-xs mt-1.5 px-1">{errors.password}</p>
            )}
          </div>

          {/* Forgot password */}
          <div className="flex justify-end -mt-1">
            <span
              className="text-xs text-[#F05A22] hover:text-[#f47244] cursor-pointer transition-colors"
              onClick={() => navigate("/forgotpassword")}
            >
              Forgot password?
            </span>
          </div>

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-3 rounded-xl text-white font-semibold text-sm mt-1 hover:opacity-90 hover:scale-[1.01] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ background: "linear-gradient(135deg, #F05A22, #E8407A)" }}
          >
            {loading ? "Signing in..." : "Continue"}
          </button>

          {/* Terms */}
          <p className="text-center text-[11px] text-white/25 mt-1 leading-relaxed">
            By continuing, you agree to Martech's{" "}
            <span className="text-white/40 underline cursor-pointer hover:text-white/60">Terms of service</span>
            {" "}and{" "}
            <span className="text-white/40 underline cursor-pointer hover:text-white/60">Privacy policy</span>.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
